import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  FormControlLabel,
} from "@mui/material";
import { Email, NotificationsActive } from "@mui/icons-material";
import axios from "axios";
import Cookies from "universal-cookie";

const API_BASE = "https://api.easyagility.co.uk";
const BRAND = "#00395A";

type MailingListSectionProps = {
  /** When set, use account email and show a switch (signed-in hub home). */
  authenticated?: boolean;
  /** Login/marketing page styling to match hub landing cards. */
  variant?: "default" | "login";
};

const MailingListSection: React.FC<MailingListSectionProps> = ({
  authenticated = false,
  variant = "default",
}) => {
  const isLogin = variant === "login";
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(authenticated);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    if (!authenticated) return;
    try {
      setStatusLoading(true);
      const token = new Cookies().get("token");
      if (!token) return;
      const { data } = await axios.get<{ email: string; subscribed: boolean }>(
        `${API_BASE}/mailing-list`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setEmail(data.email);
      setSubscribed(data.subscribed);
    } catch {
      setError("Could not load mailing list preference.");
    } finally {
      setStatusLoading(false);
    }
  }, [authenticated]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const postSubscription = async (targetEmail: string, nextSubscribed: boolean) => {
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      const { data } = await axios.post<{ email: string; subscribed: boolean }>(
        `${API_BASE}/mailing-list`,
        { email: targetEmail, subscribed: nextSubscribed },
      );
      setEmail(data.email);
      setSubscribed(data.subscribed);
      setMessage(
        data.subscribed
          ? "You will be emailed when a show goes live on Easy Agility."
          : "You have been removed from show notifications.",
      );
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number; data?: string } })
        ?.response?.status;
      if (status === 400) {
        setError("Please enter a valid email address.");
      } else {
        setError("Could not update your subscription. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (!email) return;
    await postSubscription(email, checked);
  };

  const handleGuestSubscribe = () => postSubscription(email, true);
  const handleGuestUnsubscribe = () => postSubscription(email, false);

  const paperSx = isLogin
    ? {
        p: { xs: 2.5, sm: 3 },
        borderRadius: 3,
        borderColor: `${BRAND}30`,
        bgcolor: `${BRAND}06`,
        mb: 0,
      }
    : { p: 2.5, mb: 3, borderRadius: 2 };

  const primaryButtonSx = isLogin
    ? {
        textTransform: "none" as const,
        borderRadius: 2,
        fontWeight: 600,
        bgcolor: BRAND,
        "&:hover": { bgcolor: "#002a42" },
      }
    : { textTransform: "none" as const };

  const outlinedButtonSx = isLogin
    ? {
        textTransform: "none" as const,
        borderRadius: 2,
        borderColor: BRAND,
        color: BRAND,
        fontWeight: 600,
        "&:hover": { bgcolor: `${BRAND}0a`, borderColor: BRAND },
      }
    : { textTransform: "none" as const };

  return (
    <Paper variant="outlined" sx={paperSx}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1.5 }}>
        {isLogin ? (
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: `${BRAND}12`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              "& .MuiSvgIcon-root": { fontSize: 26, color: BRAND },
            }}
          >
            <NotificationsActive />
          </Box>
        ) : (
          <NotificationsActive color="primary" fontSize="small" sx={{ mt: 0.25 }} />
        )}
        <Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: isLogin ? BRAND : "text.primary" }}
          >
            Show go-live alerts
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Get an email when entries open for a new show on Easy Agility (when the
            closing date is published).
          </Typography>
        </Box>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {message && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message}
        </Alert>
      )}

      {authenticated ? (
        <FormControlLabel
          sx={{ ml: isLogin ? 0 : undefined, mt: 0.5 }}
          control={
            <Switch
              checked={subscribed}
              onChange={handleToggle}
              disabled={loading || statusLoading || !email}
            />
          }
          label={
            statusLoading
              ? "Loading…"
              : subscribed
                ? `Notify ${email}`
                : `Email me at ${email || "your account address"} when a show goes live`
          }
        />
      ) : (
        <Stack spacing={2} sx={{ mt: isLogin ? 1 : 0 }}>
          <TextField
            size="small"
            type="email"
            label="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            useFlexGap
          >
            <Button
              variant="contained"
              disabled={loading || !email.trim()}
              onClick={handleGuestSubscribe}
              fullWidth
              sx={primaryButtonSx}
            >
              Subscribe
            </Button>
            <Button
              variant="outlined"
              disabled={loading || !email.trim()}
              onClick={handleGuestUnsubscribe}
              fullWidth
              sx={outlinedButtonSx}
            >
              Unsubscribe
            </Button>
          </Stack>
        </Stack>
      )}
    </Paper>
  );
};

export default MailingListSection;
